import { useCallback, useState } from 'react';

import type { RuleSchema, StructuredEvaluatorSchema } from '@/types/schema';

export type PaneType =
  | 'alerts'
  | 'automation'
  | 'llm'
  | 'code'
  | 'thread'
  | 'compositeScore'
  | 'clusterJobs'
  | null;

interface PaneState {
  activePane: PaneType;
}

interface UsePaneStateReturn {
  selectedRule: RuleSchema | null;
  setSelectedRule: (rule: RuleSchema | null) => void;
  prebuiltEvaluator: StructuredEvaluatorSchema | undefined;
  setPrebuiltEvaluator: (
    evaluator: StructuredEvaluatorSchema | undefined
  ) => void;
  newPopoverOpen: boolean;
  setNewPopoverOpen: (open: boolean) => void;
  paneState: PaneState;
  setPaneState: (state: PaneState) => void;
  togglePane: (
    pane: Exclude<PaneType, 'newPopover'>,
    openState: React.SetStateAction<boolean>
  ) => void;
}

export function usePaneState(): UsePaneStateReturn {
  const [selectedRule, setSelectedRule] = useState<RuleSchema | null>(null);
  const [prebuiltEvaluator, setPrebuiltEvaluator] =
    useState<StructuredEvaluatorSchema>();
  const [newPopoverOpen, setNewPopoverOpen] = useState<boolean>(false);
  const [paneState, setPaneState] = useState<PaneState>({
    activePane: null,
  });

  const togglePane = useCallback(
    (
      pane: Exclude<PaneType, 'newPopover'>,
      openState: React.SetStateAction<boolean>
    ) => {
      const shouldOpen =
        typeof openState === 'function'
          ? (openState as (v: boolean) => boolean)(false)
          : openState;

      // Always start by closing everything and clearing selected items
      setPaneState({ activePane: null });
      setSelectedRule(null);
      setPrebuiltEvaluator(undefined);
      setNewPopoverOpen(false);

      if (!shouldOpen) return;

      // set small timeout for pane sliding animation to finish before
      // opening the new pane
      setTimeout(() => {
        setPaneState({ activePane: pane });
      }, 100);
    },
    []
  );

  return {
    selectedRule,
    setSelectedRule,
    prebuiltEvaluator,
    setPrebuiltEvaluator,
    newPopoverOpen,
    setNewPopoverOpen,
    paneState,
    setPaneState,
    togglePane,
  };
}
